'use client';

import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { Dealership as DealershipRow } from '@/utils/types';
import { THUMBNAIL_ZOOM } from '@/lib/mapThumbnail';

type Dealership = DealershipRow & { phones?: string[] | null };

/**
 * Panel del mapa de sucursales.
 *
 * Gemelo del que vive en goautos-admin (src/components/map/DealershipMapPanel).
 * Si se toca uno, revisar el otro: acá se renderizan los sitios publicados y
 * allá el builder los muestra, y tienen que verse igual.
 *
 * Decisiones que conviene no deshacer sin leer esto:
 *
 * 1. El mapa interactivo se instancia SOLO cuando el visitante lo pide. Mapbox
 *    factura por `new mapboxgl.Map()`, no por tiles ni por paneo, así que tener
 *    el mapa interactivo por defecto costaba una carga por visita. Mientras
 *    tanto se muestra una miniatura estática cacheada (ver /api/map-static),
 *    que se ve igual y no consume cupo.
 *
 * 2. `mapbox-gl` se importa dinámicamente dentro del handler. Son ~488 KB gz:
 *    con import estático los cargarían TODOS los sitios, incluso los que no
 *    tienen esta sección. Además toca `window` al importarse y este componente
 *    se renderiza desde rutas con SSR.
 *
 * 3. Cambiar de sucursal hace `flyTo()` sobre la instancia existente. Remontar
 *    el mapa contaría como una carga nueva en cada click de la flechita.
 */

type MapState = 'idle' | 'loading' | 'ready' | 'error';

interface DealershipMapPanelProps {
  dealerships: Dealership[];
  selectedDealership: Dealership | null;
  onSelect: (dealership: Dealership) => void;
  onOpenDirections: (dealership: Dealership) => void;
  height: string;
  clientId?: string;
}

const MARKER_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="currentColor">' +
  '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>';

// `block h-8 w-8` es lo que hace que el pin caiga donde debe. Mapbox centra el
// ELEMENTO del marker sobre la coordenada, y un <div> sin ancho propio se estira
// a todo el contenedor: el div quedaba de 801px, Mapbox lo centraba bien, pero
// el SVG de 32px quedaba pegado a su borde izquierdo — el pin visible aparecía
// ~384px corrido, y como ese ancho depende del tamaño del mapa, el desfase
// cambiaba al hacer zoom y parecía que el pin flotaba sin apuntar a nada.
// La transición se limita a opacidad: si incluyera `transform`, animaría el
// reposicionamiento que Mapbox aplica en cada frame del paneo.
const MARKER_BASE =
  'block h-8 w-8 cursor-pointer text-blue-500 drop-shadow-lg transition-opacity duration-200';

function coordsOf(dealership: Dealership | null): [number, number] | null {
  const lat = Number(dealership?.location?.lat);
  const lng = Number(dealership?.location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lng, lat];
}

/**
 * Réplica del resaltado que hacía el <OverlayView> de Google.
 *
 * El destacado va por tamaño y no por `scale-*`: Mapbox escribe el `transform`
 * del elemento en cada reposicionamiento, así que cualquier transform propio
 * queda pisado o pelea con el posicionamiento.
 */
function markerClass(isSelected: boolean, hasMultiple: boolean): string {
  if (!hasMultiple) return MARKER_BASE;
  return isSelected
    ? MARKER_BASE.replace('h-8 w-8', 'h-9 w-9')
    : `${MARKER_BASE} opacity-40 hover:opacity-70`;
}

export default function DealershipMapPanel({
  dealerships,
  selectedDealership,
  onSelect,
  onOpenDirections,
  height,
  clientId,
}: DealershipMapPanelProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<MapState>('idle');
  const [thumbFailed, setThumbFailed] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const mapboxglRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; el: HTMLElement; id: string | number }[]>([]);
  const loadedRef = useRef(false);
  // Guard contra doble instanciación: `state` no sirve porque depende del ciclo
  // de render, y cada `new mapboxgl.Map()` se factura.
  const startedRef = useRef(false);
  // El handler de los markers se lee desde un ref para no tener que recrearlos.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';
  const selectedCoords = coordsOf(selectedDealership);
  const [lng, lat] = selectedCoords ?? [undefined, undefined];
  const hasCoords = lng !== undefined && lat !== undefined;
  const hasMultiple = dealerships.filter((d) => coordsOf(d)).length > 1;
  const dealershipsKey = dealerships
    .map((d) => `${d.id}:${d.location?.lat},${d.location?.lng}`)
    .join('|');

  // La `v` no la usa el servidor: está para que el CDN trate la miniatura de
  // unas coordenadas nuevas como una URL distinta. Sin esto, un cliente que
  // corrige la dirección de su sucursal seguiría viendo la foto vieja durante
  // un año, mientras el mapa interactivo ya muestra la ubicación buena.
  const thumbnailUrl =
    selectedDealership && hasCoords
      ? selectedDealership.id === 'default' && clientId
        ? `/api/map-static?c=${encodeURIComponent(clientId)}&v=${lng},${lat},${THUMBNAIL_ZOOM}`
        : `/api/map-static?d=${encodeURIComponent(selectedDealership.id)}&v=${lng},${lat},${THUMBNAIL_ZOOM}`
      : null;

  // Que falle la miniatura de una sucursal no puede dejar ciegas a las demás.
  useEffect(() => {
    setThumbFailed(false);
  }, [thumbnailUrl]);

  const loadMap = useCallback(async () => {
    if (startedRef.current) return;
    if (!token || !hasCoords) {
      setState('error');
      return;
    }
    startedRef.current = true;

    setState('loading');
    try {
      const [{ default: mapboxgl }] = await Promise.all([
        import('mapbox-gl'),
        // El CSS se importa acá y no arriba para que no viaje en el bundle de
        // los sitios cuyos visitantes nunca abren el mapa.
        import('mapbox-gl/dist/mapbox-gl.css'),
      ]);

      if (!containerRef.current) return;

      mapboxgl.accessToken = token;
      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [lng as number, lat as number],
        zoom: THUMBNAIL_ZOOM,
        attributionControl: true, // obligatorio por licencia de Mapbox
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      // Sólo los errores ANTERIORES al load son fatales. Mapbox emite 'error'
      // también por un tile suelto que no bajó; si eso tumbara el panel, a un
      // visitante en 4G se le desaparecería un mapa que venía andando, sin
      // forma de reintentar (la instancia ya está creada y cobrada).
      map.on('error', () => {
        if (!loadedRef.current) setState('error');
      });
      map.on('load', () => {
        loadedRef.current = true;
        setState('ready');
        // Cinturón: mapbox mide el contenedor al construirse, y en ese momento
        // el div todavía puede estar oculto o sin su tamaño final.
        map.resize();
      });

      mapboxglRef.current = mapboxgl;
      mapRef.current = map;
    } catch {
      // WebGL deshabilitado, red caída, token rechazado: da igual cuál sea, el
      // visitante tiene que quedarse igual con la dirección y el link.
      startedRef.current = false;
      setState('error');
    }
  }, [token, hasCoords, lng, lat]);

  // Los markers se reconstruyen cuando cambian las sucursales. Con los
  // <OverlayView> de Google esto era declarativo y salía gratis; acá hay que
  // hacerlo a mano, o el pin queda en la posición vieja tras editar la sede.
  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = mapboxglRef.current;
    if (state !== 'ready' || !map || !mapboxgl) return;

    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = [];

    dealerships.forEach((dealership) => {
      const coords = coordsOf(dealership);
      if (!coords) return;
      const el = document.createElement('div');
      el.innerHTML = MARKER_SVG;
      el.className = MARKER_BASE;
      el.addEventListener('click', () => onSelectRef.current(dealership));
      markersRef.current.push({
        // anchor 'bottom': el SVG es un pin con la punta abajo, así que es la
        // punta la que tiene que caer sobre la coordenada, no el centro del ícono.
        marker: new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat(coords)
          .addTo(map),
        el,
        id: dealership.id,
      });
    });
  }, [state, dealershipsKey]);

  // Resaltar el pin de la sucursal elegida sin recrear los markers.
  useEffect(() => {
    markersRef.current.forEach(({ el, id }) => {
      el.className = markerClass(id === selectedDealership?.id, hasMultiple);
    });
  }, [state, dealershipsKey, selectedDealership?.id, hasMultiple]);

  // Recentrar sin recrear el mapa.
  useEffect(() => {
    if (state !== 'ready' || !mapRef.current || !hasCoords) return;
    mapRef.current.flyTo({ center: [lng as number, lat as number], zoom: THUMBNAIL_ZOOM, duration: 800 });
  }, [state, hasCoords, lng, lat]);

  useEffect(() => {
    return () => {
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  const showInteractive = state === 'loading' || state === 'ready';
  // Sin coordenadas el botón sólo llevaría al cartel de error: mejor no darlo.
  const showFallback = state === 'error' || (state === 'idle' && !hasCoords);

  return (
    <div
      style={{ height }}
      className="relative w-full rounded-xl overflow-hidden shadow-sm border border-slate-200/60 dark:border-dark-border bg-slate-100 dark:bg-dark-card"
    >
      {/* El contenedor queda siempre montado para que mapbox tenga dónde dibujar */}
      {/* `h-full w-full` no es redundante con `inset-0`: mapbox-gl le agrega la
          clase `mapboxgl-map`, que trae `position: relative`, y su CSS entra
          después (import dinámico), así que pisa el `absolute` de Tailwind. Sin
          `absolute`, `inset-0` deja de dimensionar y el contenedor colapsa a
          altura 0 — el mapa se veía como una franja de unos pocos píxeles. */}
      <div
        ref={containerRef}
        className="absolute inset-0 h-full w-full"
        hidden={!showInteractive}
      />

      {state === 'loading' && (
        <div
          role="status"
          className="absolute inset-0 z-10 flex items-center justify-center bg-slate-100/80 dark:bg-dark-card/80"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary" />
        </div>
      )}

      {state === 'idle' && hasCoords && (
        <button
          type="button"
          onClick={loadMap}
          className="group absolute inset-0 h-full w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
          aria-label={t('home.howToArrive.showMap')}
        >
          {thumbnailUrl && !thumbFailed ? (
            <img
              src={thumbnailUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
              onError={() => setThumbFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-100 dark:bg-dark-card">
              <Icon icon="mdi:map-marker" className="text-5xl text-slate-300 dark:text-slate-600" />
            </div>
          )}
          {/* El botón va abajo, no centrado: en el centro tapa justo el pin,
              que es lo único que el visitante necesita ver de un vistazo. Y sin
              velo encima, que sólo le bajaba contraste al mapa. */}
          <span className="absolute inset-x-0 bottom-0 flex justify-center pb-7 pt-10 bg-gradient-to-t from-black/25 to-transparent">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-gray-900 shadow-md transition-colors group-hover:bg-white dark:bg-dark-card/95 dark:text-white">
              <Icon icon="mdi:map-search-outline" className="text-lg" />
              {t('home.howToArrive.showMap')}
            </span>
          </span>
          {/* Atribución obligatoria: la miniatura es una imagen de Mapbox y es
              lo que ve la mayoría, porque el mapa interactivo pide un click. */}
          <span className="absolute bottom-1 right-2 text-[10px] text-slate-500/80">
            © Mapbox © OpenStreetMap
          </span>
        </button>
      )}

      {showFallback && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <Icon icon="mdi:map-marker-off-outline" className="text-4xl text-slate-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('home.howToArrive.mapUnavailable')}
          </p>
          {selectedDealership?.address && (
            <p className="text-sm text-gray-900 dark:text-white">{selectedDealership.address}</p>
          )}
          {selectedDealership && hasCoords && (
            <button
              type="button"
              onClick={() => onOpenDirections(selectedDealership)}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t('home.howToArrive.button')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
